<template>
  <!--
    รายงานครูและบุคลากรสายการสอน รายประเภทวิชา/สาขาวิชา (ข้าราชการครู · พนง.ราชการสายการสอน · ครูอัตราจ้าง)
    ข้อมูลชุดเดียวจาก personnel_etc_branch_report.php → Excel (.xlsx สร้างฝั่งเว็บ) และ PDF (mPDF ฝั่งเซิร์ฟเวอร์)
  -->
  <v-dialog :value="value" max-width="780" scrollable @input="$emit('input', $event)">
    <v-card class="pbr">
      <!-- ใช้ div แทน v-card-title (ธีมโปรเจกต์จัดกึ่งกลาง title) -->
      <div class="pbr-head">
        <v-icon color="white" class="mr-3">mdi-file-table-box-multiple-outline</v-icon>
        <div class="pbr-head-text">
          <div class="pbr-title">รายงานครูและบุคลากรสายการสอน รายสาขา</div>
          <div class="pbr-subtitle">ข้าราชการครู · พนักงานราชการสายการสอน · ครูอัตราจ้าง</div>
        </div>
        <v-btn icon dark small @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="pt-4">
        <!-- พื้นที่ -->
        <div class="pbr-label">พื้นที่</div>
        <v-btn-toggle v-model="scope" mandatory dense color="#1f3b57" class="mb-4 pbr-toggle" @change="load">
          <v-btn value="nation" small><v-icon small left>mdi-earth</v-icon>ทั่วประเทศ</v-btn>
          <v-btn value="filter" small :disabled="!filterActive">
            <v-icon small left>mdi-filter-outline</v-icon>
            ตามตัวกรอง{{ filterActive ? " (" + filterLabel + ")" : "" }}
          </v-btn>
        </v-btn-toggle>

        <!-- รายงาน -->
        <div class="pbr-label">เลือกรายงาน</div>
        <div class="pbr-reports mb-4">
          <div v-for="r in reports" :key="r.value" class="pbr-report" :class="{ active: report === r.value }"
            @click="report = r.value">
            <v-icon :color="report === r.value ? '#c62828' : '#6b7785'">{{ r.icon }}</v-icon>
            <div>
              <div class="pbr-report-title">{{ r.title }}</div>
              <div class="pbr-report-desc">{{ r.desc }}</div>
            </div>
          </div>
        </div>

        <!-- ตัวอย่างตัวเลข -->
        <div class="pbr-label">
          ตัวอย่างข้อมูล
          <span v-if="data" class="pbr-asof">· {{ data.area_label }} · ณ {{ data.as_of }}</span>
        </div>
        <v-skeleton-loader v-if="loading" type="table-row@3" />
        <v-alert v-else-if="error" type="error" dense outlined class="mb-0">{{ error }}</v-alert>
        <div v-else-if="data" class="pbr-preview">
          <div v-for="o in data.overview.rows" :key="o.type" class="pbr-stat">
            <div class="pbr-stat-n">{{ fmt(o.n) }}</div>
            <div class="pbr-stat-l">{{ o.label }}</div>
            <div class="pbr-stat-s">{{ fmt(o.colleges) }} สถานศึกษา · ระบุสาขาแล้ว {{ pct(o.with_branch, o.n) }}%</div>
          </div>
          <div class="pbr-stat total">
            <div class="pbr-stat-n">{{ fmt(data.grand.total) }}</div>
            <div class="pbr-stat-l">รวม</div>
            <div class="pbr-stat-s">{{ data.subjects.length }} ประเภทวิชา · {{ branchCount }} สาขาวิชา</div>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <span class="pbr-note">ร้อยละคิดจากจำนวนรวมทั้งสิ้น · สาขาชื่อเดียวกันระดับ ปวช./ปวส. นับรวมกัน</span>
        <v-spacer />
        <v-btn color="#1b5e20" dark depressed :loading="busy === 'xlsx'" :disabled="!data || !!busy" @click="downloadXlsx">
          <v-icon left small>mdi-microsoft-excel</v-icon>Excel (.xlsx)
        </v-btn>
        <v-btn color="#c62828" dark depressed :loading="busy === 'pdf'" :disabled="!!busy" @click="openPdf">
          <v-icon left small>mdi-file-pdf-box</v-icon>PDF
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/* global Swal */ // SweetAlert2 โหลดแบบ global ใน index.html (เหมือนหน้าอื่น)
const API = "personnel_etc_branch_report.php";
const REPORTS = [
  { value: "all", icon: "mdi-book-open-page-variant-outline", title: "ครบทุกรายงาน", desc: "ภาพรวม + ประเภทวิชา + สาขาวิชา" },
  { value: "overview", icon: "mdi-chart-donut", title: "1. ภาพรวม", desc: "รายกลุ่มบุคลากร และรายภาค" },
  { value: "subject", icon: "mdi-shape-outline", title: "2. จำแนกตามประเภทวิชา", desc: "อุตสาหกรรม พาณิชยกรรม ฯลฯ" },
  { value: "branch", icon: "mdi-file-tree-outline", title: "3. จำแนกตามสาขาวิชา", desc: "รายสาขาภายใต้แต่ละประเภทวิชา" }
];
const REPORT_NAMES = { all: "ครบทุกรายงาน", overview: "ภาพรวม", subject: "ประเภทวิชา", branch: "สาขาวิชา" };

export default {
  name: "PersonnelBranchReportDialog",
  props: {
    value: { type: Boolean, default: false },
    regionId: { type: [String, Number], default: "" },
    provinceId: { type: [String, Number], default: "" },
    filterLabel: { type: String, default: "" }
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      reports: REPORTS,
      report: "all",
      scope: "nation",
      data: null,
      loading: false,
      error: "",
      busy: ""
    };
  },
  computed: {
    filterActive() {
      return !!this.regionId || !!this.provinceId;
    },
    branchCount() {
      if (!this.data) return 0;
      const names = new Set();
      this.data.branches.forEach(g => g.rows.forEach(b => names.add(b.name)));
      return names.size;
    }
  },
  watch: {
    value(open) {
      if (open) {
        if (!this.filterActive) this.scope = "nation";
        this.load();
      }
    }
  },
  methods: {
    fmt(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    pct(a, b) {
      return b ? ((a * 100) / b).toFixed(1) : "0.0";
    },
    payload(extra) {
      const p = Object.assign({ ApiKey: this.ApiKey, report: this.report }, extra);
      if (this.scope === "filter") {
        if (this.regionId) p.filter_region_ID = this.regionId;
        if (this.provinceId) p.filter_province_ID = this.provinceId;
      }
      return p;
    },
    async load() {
      this.loading = true;
      this.error = "";
      this.data = null;
      try {
        const res = await this.$http.post(API, this.payload({ format: "json", report: "all" }));
        if (!res.data || !res.data.status) throw new Error((res.data && res.data.message) || "โหลดข้อมูลไม่สำเร็จ");
        this.data = res.data;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message;
      } finally {
        this.loading = false;
      }
    },
    fileBase() {
      const area = this.data ? this.data.area_label.replace(/[\\/:*?[\]]/g, "") : "";
      const d = new Date();
      const ymd = d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0");
      return "รายงานครูสายการสอน_" + REPORT_NAMES[this.report] + "_" + area + "_" + ymd;
    },

    // ---------------------------------------------------------------- PDF
    async openPdf() {
      const win = window.open("", "_blank"); // เปิดก่อน await กัน popup blocker
      if (win) win.document.write("<p style='font-family:sans-serif'>กำลังสร้างรายงาน PDF ...</p>");
      this.busy = "pdf";
      try {
        const res = await this.$http.post(API, this.payload({ format: "pdf" }), { responseType: "blob" });
        if (!(res.headers["content-type"] || "").includes("pdf")) {
          throw new Error(JSON.parse(await res.data.text()).message);
        }
        const url = URL.createObjectURL(res.data);
        if (win) win.location.href = url;
        else window.open(url, "_blank");
      } catch (e) {
        if (win) win.close();
        let msg = e.message;
        if (e.response && e.response.data instanceof Blob) {
          try {
            msg = JSON.parse(await e.response.data.text()).message;
          } catch (x) {
            // ใช้ข้อความเดิม
          }
        }
        Swal.fire({ icon: "error", title: "สร้าง PDF ไม่สำเร็จ", text: msg });
      } finally {
        this.busy = "";
      }
    },

    // ---------------------------------------------------------------- Excel
    async loadXLSX() {
      if (window.XLSX) return;
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    },
    async downloadXlsx() {
      this.busy = "xlsx";
      try {
        await this.loadXLSX();
        const X = window.XLSX;
        const wb = X.utils.book_new();
        const r = this.report;
        if (r === "all" || r === "overview") X.utils.book_append_sheet(wb, this.sheetOverview(X), "1 ภาพรวม");
        if (r === "all" || r === "subject") X.utils.book_append_sheet(wb, this.sheetSubject(X), "2 ประเภทวิชา");
        if (r === "all" || r === "branch") X.utils.book_append_sheet(wb, this.sheetBranch(X), "3 สาขาวิชา");
        X.writeFile(wb, this.fileBase() + ".xlsx");
      } catch (e) {
        Swal.fire({ icon: "error", title: "สร้าง Excel ไม่สำเร็จ", text: e.message || "" });
      } finally {
        this.busy = "";
      }
    },
    typeKeys() {
      return this.data.types.map(t => t.key);
    },
    typeLabels() {
      return this.data.types.map(t => t.label);
    },
    share(n) {
      const g = this.data.grand.total;
      return g ? Math.round((n * 10000) / g) / 100 : 0;
    },
    titleRows(title) {
      return [
        ["สำนักงานคณะกรรมการการอาชีวศึกษา"],
        [title],
        [this.data.area_label + " · ข้อมูล ณ วันที่ " + this.data.as_of],
        []
      ];
    },
    /** aoa → sheet + ความกว้างคอลัมน์ + merge แถวหัวเรื่อง + รูปแบบตัวเลข */
    toSheet(X, aoa, widths, pctCols) {
      const ws = X.utils.aoa_to_sheet(aoa);
      ws["!cols"] = widths.map(w => ({ wch: w }));
      ws["!merges"] = [0, 1, 2].map(row => ({ s: { r: row, c: 0 }, e: { r: row, c: widths.length - 1 } }));
      const range = X.utils.decode_range(ws["!ref"]);
      for (let R = 4; R <= range.e.r; R++) {
        for (let C = 0; C <= range.e.c; C++) {
          const cell = ws[X.utils.encode_cell({ r: R, c: C })];
          if (cell && cell.t === "n") cell.z = pctCols.includes(C) ? "0.00" : "#,##0";
        }
      }
      return ws;
    },
    sheetOverview(X) {
      const d = this.data;
      const keys = this.typeKeys();
      const aoa = this.titleRows("รายงานข้อมูลครูและบุคลากรสายการสอน — 1. ภาพรวม");
      aoa.push(["ที่", "กลุ่มบุคลากร", "จำนวน (คน)", "ร้อยละ", "สถานศึกษา (แห่ง)", "ระบุประเภทวิชาแล้ว", "ระบุสาขาวิชาแล้ว"]);
      d.overview.rows.forEach((o, i) =>
        aoa.push([i + 1, o.label, o.n, this.share(o.n), o.colleges, o.with_subject, o.with_branch])
      );
      const t = d.overview.total;
      aoa.push(["", "รวม", t.n, d.grand.total ? 100 : 0, t.colleges, t.with_subject, t.with_branch]);
      aoa.push([]);
      aoa.push(["จำแนกตามภาค"]);
      aoa.push(["ที่", "ภาค"].concat(this.typeLabels(), ["รวม", "ร้อยละ"]));
      d.overview.regions.forEach((rg, i) =>
        aoa.push([i + 1, rg.name].concat(keys.map(k => rg.counts[k]), [rg.total, this.share(rg.total)]))
      );
      aoa.push(["", "รวม"].concat(keys.map(k => d.grand.counts[k]), [d.grand.total, d.grand.total ? 100 : 0]));
      // ร้อยละ: ตารางกลุ่มบุคลากรอยู่คอลัมน์ 3 · ตารางรายภาคอยู่คอลัมน์ 6 → แก้รูปแบบเฉพาะแถวของตารางรายภาค
      const ws = this.toSheet(X, aoa, [6, 34, 16, 16, 16, 18, 18], [3]);
      const regionStart = 4 + 1 + d.overview.rows.length + 1 + 2 + 1; // หัวเรื่อง4 + หัวตาราง + แถว + รวม + ว่าง/ชื่อตาราง + หัวตาราง
      for (let R = regionStart; R < aoa.length; R++) {
        const cell = ws[X.utils.encode_cell({ r: R, c: 6 })];
        if (cell && cell.t === "n") cell.z = "0.00";
        const c3 = ws[X.utils.encode_cell({ r: R, c: 3 })];
        if (c3 && c3.t === "n") c3.z = "#,##0";
      }
      return ws;
    },
    sheetSubject(X) {
      const d = this.data;
      const keys = this.typeKeys();
      const aoa = this.titleRows("รายงานข้อมูลครูและบุคลากรสายการสอน — 2. จำแนกตามประเภทวิชา");
      aoa.push(["ที่", "รหัส", "ประเภทวิชา"].concat(this.typeLabels(), ["รวม", "ร้อยละ", "สถานศึกษา (แห่ง)"]));
      d.subjects.forEach((s, i) =>
        aoa.push([i + 1, s.code, s.name].concat(keys.map(k => s.counts[k]), [s.total, this.share(s.total), s.colleges]))
      );
      aoa.push(["", "", "รวม"].concat(keys.map(k => d.grand.counts[k]), [d.grand.total, d.grand.total ? 100 : 0, d.overview.total.colleges]));
      return this.toSheet(X, aoa, [6, 7, 38, 15, 22, 15, 12, 10, 16], [7]);
    },
    sheetBranch(X) {
      const d = this.data;
      const keys = this.typeKeys();
      const aoa = this.titleRows("รายงานข้อมูลครูและบุคลากรสายการสอน — 3. จำแนกตามสาขาวิชา");
      // หนึ่งแถวต่อสาขา มีคอลัมน์ประเภทวิชาทุกแถว → ใช้ตัวกรอง/Pivot ใน Excel ได้ทันที
      aoa.push(["ที่", "รหัสประเภทวิชา", "ประเภทวิชา", "สาขาวิชา"].concat(this.typeLabels(), ["รวม", "ร้อยละ", "สถานศึกษา (แห่ง)"]));
      let i = 0;
      d.branches.forEach(g => {
        g.rows.forEach(b => {
          i++;
          aoa.push([i, g.code, g.name, b.name].concat(keys.map(k => b.counts[k]), [b.total, this.share(b.total), b.colleges]));
        });
        const st = g.subtotal;
        aoa.push(["", g.code, g.name, "รวมประเภทวิชา" + g.name].concat(keys.map(k => st.counts[k]), [st.total, this.share(st.total), st.colleges]));
      });
      aoa.push(["", "", "", "รวมทั้งสิ้น"].concat(keys.map(k => d.grand.counts[k]), [d.grand.total, d.grand.total ? 100 : 0, d.overview.total.colleges]));
      return this.toSheet(X, aoa, [6, 10, 30, 40, 15, 22, 15, 12, 10, 16], [8]);
    }
  }
};
</script>

<style scoped>
.pbr-head {
  display: flex;
  align-items: center;
  background: #1f3b57;
  color: #fff;
  padding: 14px 18px;
  text-align: left;
}
.pbr-head-text {
  flex: 1 1 auto;
  min-width: 0;
}
.pbr-title {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}
.pbr-subtitle {
  font-size: 0.8rem;
  opacity: 0.85;
}
.pbr-label {
  font-weight: 700;
  color: #1f3b57;
  font-size: 0.86rem;
  margin-bottom: 6px;
}
.pbr-asof {
  font-weight: 400;
  color: #6b7785;
}
.pbr-toggle .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
.pbr-reports {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.pbr-report {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  background: #fafbfc;
  transition: border-color 0.15s, background 0.15s;
}
.pbr-report:hover {
  border-color: #b8c4d0;
}
.pbr-report.active {
  border-color: #c62828;
  background: #fdecec;
}
.pbr-report-title {
  font-weight: 700;
  color: #1f3b57;
  font-size: 0.88rem;
}
.pbr-report-desc {
  color: #6b7785;
  font-size: 0.76rem;
}
.pbr-preview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
.pbr-stat {
  border: 1px solid #e3e8ee;
  border-top: 3px solid #b8c4d0;
  border-radius: 8px;
  padding: 8px 10px;
}
.pbr-stat.total {
  border-top-color: #1f3b57;
  background: #f4f6f9;
}
.pbr-stat-n {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f3b57;
}
.pbr-stat-l {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2f3d4a;
}
.pbr-stat-s {
  font-size: 0.72rem;
  color: #6b7785;
}
.pbr-note {
  font-size: 0.74rem;
  color: #6b7785;
}
@media (max-width: 700px) {
  .pbr-reports,
  .pbr-preview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .pbr-note {
    display: none;
  }
}
</style>
